import React from 'react';
import { useForm } from '@weezevent/weezjs-core';
import LoadingCard from '../../../../../components/loadingCard';
import { ToastContext, Button, Modal, Message, SelectLabel, SelectHierarchyLabel } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

export default function ZoneDeletionModal({ zone = null, onSubmit, onClose, organizationId, chartId, submitting }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [loading, setLoading] = React.useState(false);

    const [form, setForm] = useForm({
        deletionMode: 0,
        availableInventories: [],
        selectedInventories: [],
        deletionInfo: null
    });

    const deletionMode = React.useMemo(() => {
        return [
            { text: t('ticket.charts.zone.propagate.options.all'), value: 0 },
            { text: t('ticket.charts.zone.propagate.options.manual'), value: 1 }
        ];
    });

    React.useEffect(() => {
        if (form.deletionInfo || form.deletionInfo === false || !zone) {
            return;
        }
        setLoading(true);
        zone.getUsageInfo({ organizationId, chartId })
            .then(info => {
                setForm('deletionInfo', info);
                const defaultSelected = Object.entries(info.inventories)
                    .map(([id, { bookings, contingents }]) => {
                        if (!bookings && !contingents) {
                            return id;
                        }

                        return null;
                    })
                    .filter(e => e);
                if (defaultSelected.length > 0) {
                    setForm('selectedInventories', defaultSelected);
                }
            })
            .catch(e => {
                onClose();
                toast.error(t('ticket.charts.zone.usageInfoRetrievalError'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [zone]);

    if (!zone) {
        return null;
    }

    return (
        <Modal
            size={'large'}
            open={Boolean(zone)}
            allowExternalClosing={false}
            onClose={onClose}
            title={t('ticket.zones.deletionTitle', { name: zone.name })}
            submitButton={<Button primary disabled={loading || submitting} onClick={() => onSubmit(form.selectedInventories)} label={t('ticket.common.action.save')} />}
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} />}
        >
            {zone.children?.length && (
                <Message title={t('ticket.common.warning')} type="danger">
                    {t('ticket.charts.zone.deletion_warning.sub_zones')}
                </Message>
            )}
            <Message title={t('ticket.common.furtherInformation')} type="warning">
                {t('ticket.charts.zone.deletion_warning.bookings')}
            </Message>
            <LoadingCard loading={loading || submitting}>
                <SelectLabel
                    label={t('ticket.charts.zone.deletionMode')}
                    value={form.deletionMode}
                    options={deletionMode}
                    onChange={value => setForm('deletionMode', value)}
                    useNew
                />
                <InventoriesBlock form={form} setForm={setForm} />
            </LoadingCard>
        </Modal>
    );
}

export function InventoriesBlock({ form, setForm }) {
    const { t } = useTranslation();

    const inventoriesOptions = React.useMemo(() => {
        if (!form.deletionInfo) {
            return [];
        }
        return Object.entries(form.deletionInfo?.inventories)
            .map(([iv, { name, bookings }]) => {
                return { text: name, value: iv, bookings };
            })
            .filter(iv => iv.bookings === 0);
    }, [form]);

    if (form.deletionMode !== 1) {
        return null;
    }

    return (
        <SelectHierarchyLabel
            multi
            height="auto"
            searchable={false}
            placeholder={t('ticket.common.action.select')}
            options={inventoriesOptions}
            value={form.selectedInventories.length ? t('ticket.charts.chart.inventories_selected', { count: form.selectedInventories.length }) : null}
            selected={form.selectedInventories}
            label={t('ticket.common.inventories')}
            totalItems={form.selectedInventories?.length}
            onChange={selected => setForm('selectedInventories', selected)}
            labelApply={t('ticket.common.action.select')}
            labelSelectAll={t('ticket.common.action.select-all')}
        />
    );
}
